"use client";

// src/index.ts
import { useCallback, useEffect, useRef, useState, useTransition } from "react";

// src/optimistic.ts
var evaluateOptimisticInput = (fn, oldResult, result) => {
  function isFunction(value) {
    return typeof value === "function";
  }
  return isFunction(fn) ? fn(oldResult.status === "empty" ? result.data : oldResult.result.data) : fn;
};

// src/results.ts
var calculateResultFromState = (state) => {
  const { isPending, oldResult, result } = state;
  if (isPending && oldResult.status === "empty") {
    return {
      isPending: true,
      isOptimistic: false,
      data: state.persistDataWhilePending ? result.data : void 0,
      isError: false,
      error: state.persistErrorWhilePending ? result.error : void 0,
      isSuccess: false,
      status: "pending"
    };
  } else if (isPending && oldResult.status === "filled" && result.status === "success") {
    return {
      isPending: true,
      isOptimistic: true,
      data: result.data,
      isError: false,
      error: void 0,
      isSuccess: false,
      status: "pending"
    };
  } else if (result.status === "success") {
    return {
      isPending: false,
      isOptimistic: false,
      data: result.data,
      isError: false,
      error: void 0,
      isSuccess: true,
      status: "success"
    };
  } else if (result.status === "error") {
    return {
      isPending: false,
      data: void 0,
      isError: true,
      error: result.error,
      isOptimistic: false,
      isSuccess: false,
      status: "error"
    };
  } else {
    return {
      isPending: false,
      data: void 0,
      isOptimistic: false,
      isError: false,
      error: void 0,
      isSuccess: false,
      status: "idle"
    };
  }
};
var getEmptyResult = (initialData) => initialData === void 0 ? (
  // if there is no initial data
  {
    status: "idle",
    error: void 0,
    data: void 0
  }
) : {
  // if there is initial data
  status: "success",
  error: void 0,
  data: initialData
};
var getEmptyOldResult = () => ({
  status: "empty",
  result: void 0
});

// src/retries.ts
var getRetryDelay = (retryConfig, retryCount, err) => {
  const shouldRetry = retryConfig ? retryCount + 1 < retryConfig.maxAttempts : false;
  let retryDelay = 0;
  const retryDelayOpt = retryConfig?.delay;
  if (retryDelayOpt && typeof retryDelayOpt === "function") {
    retryDelay = retryDelayOpt(retryCount + 1, err);
  } else if (retryDelayOpt && typeof retryDelayOpt === "number") {
    retryDelay = retryDelayOpt;
  }
  if (!shouldRetry)
    return -1;
  return retryDelay;
};

// src/utils.ts
var mergePossibleObjects = (obj1, obj2) => {
  if (obj1 === void 0 && obj2 === void 0) {
    return void 0;
  }
  if (obj1 === void 0)
    return obj2;
  if (obj2 === void 0)
    return obj1;
  if (typeof obj1 !== "object" || typeof obj2 !== "object") {
    return obj2;
  }
  return {
    ...obj1,
    ...obj2
  };
};

// src/index.ts
var useServerAction = (serverAction, opts) => {
  const initialData = opts?.initialData;
  const bindArgs = opts?.bind;
  const [result, $setResult] = useState(
    getEmptyResult(initialData)
  );
  const resultRef = useRef(
    getEmptyResult(initialData)
  );
  const [oldResult, $setOldResult] = useState(getEmptyOldResult());
  const oldResultRef = useRef(getEmptyOldResult());
  const lastRetryId = useRef(0);
  const retryCount = useRef(0);
  const executeRef = useRef();
  const [isTransitioning, startTransition] = useTransition();
  const [isExecuting, setExecuting] = useState(false);
  const status = useRef("idle");
  const setResult = useCallback(
    (result2) => {
      $setResult(result2);
      resultRef.current = result2;
    },
    [$setResult]
  );
  const setOldResult = useCallback(
    (oldResult2) => {
      $setOldResult(oldResult2);
      oldResultRef.current = oldResult2;
    },
    [$setOldResult]
  );
  const internalExecute = useCallback(
    async (input, overrideData, args) => {
      const { isFromRetryId } = args || {};
      if (isFromRetryId && lastRetryId.current !== isFromRetryId) {
        return [
          null,
          {
            message: "Could not successfully execute the server action",
            data: "Could not successfully execute the server action",
            stack: "",
            name: "ZSAError",
            code: "ERROR"
          }
        ];
      }
      if (!isFromRetryId) {
        retryCount.current = 0;
      }
      const retryId = Math.floor(Math.random() * 1e4);
      lastRetryId.current = retryId;
      if (opts?.onStart)
        opts.onStart();
      status.current = "pending";
      setExecuting(true);
      let data, err;
      await serverAction(input, overrideData).then((response) => {
        if (response) {
          ;
          [data, err] = response;
        }
      });
      if (err) {
        let retryDelay = getRetryDelay(opts?.retry, retryCount.current, err);
        if (retryDelay >= 0) {
          retryCount.current += 1;
          return await new Promise(
            (resolve) => setTimeout(() => {
              internalExecute(input, overrideData, {
                ...args || {},
                isFromRetryId: retryId
              }).then(resolve);
            }, retryDelay)
          );
        }
        if (oldResult.status === "filled") {
          setResult(oldResult.result);
        } else {
          setResult({
            error: err,
            data: void 0,
            status: "error"
          });
        }
        setOldResult({
          status: "empty",
          result: void 0
        });
        status.current = "error";
        setExecuting(false);
        return [data, err];
      }
      const res = {
        error: void 0,
        data: data ?? void 0,
        status: "success"
      };
      setResult(res);
      setOldResult({
        status: "empty",
        result: void 0
      });
      status.current = "success";
      setExecuting(false);
      return [data, err];
    },
    [serverAction]
  );
  const execute = useCallback(
    async (...opts2) => {
      return await new Promise((resolve) => {
        executeRef.current = resolve;
        startTransition(() => {
          internalExecute(opts2[0], mergePossibleObjects(bindArgs, opts2[1]));
        });
      });
    },
    [internalExecute]
  );
  const executeFormAction = useCallback(
    async (...opts2) => {
      return await new Promise((resolve) => {
        startTransition(() => {
          internalExecute(opts2[0], bindArgs);
        });
        executeRef.current = resolve;
        resolve(null);
      });
    },
    [internalExecute]
  );
  const setOptimistic = useCallback(
    async (fn) => {
      const data = evaluateOptimisticInput(
        fn,
        oldResultRef.current,
        resultRef.current
      );
      if (oldResult.status === "empty") {
        setOldResult({
          status: "filled",
          result: { ...result }
        });
      }
      setResult({
        error: void 0,
        data: data ?? void 0,
        status: "success"
      });
    },
    [execute]
  );
  const reset = useCallback(() => {
    setResult(getEmptyResult());
    setOldResult(getEmptyOldResult());
    setExecuting(false);
    status.current = "idle";
    lastRetryId.current = 0;
    retryCount.current = 0;
  }, []);
  const isRunningCallbacks = useRef(false);
  const handleCallbacks = useCallback(() => {
    if (!executeRef.current || isRunningCallbacks.current) {
      return;
    }
    isRunningCallbacks.current = true;
    if (status.current === "success") {
      executeRef.current?.([resultRef.current.data, null]);
      opts?.onSuccess?.({
        data: resultRef.current.data
      });
      opts?.onFinish?.([resultRef.current.data, null]);
    }
    if (status.current === "error") {
      executeRef.current?.([null, resultRef.current.error]);
      opts?.onError?.({
        err: resultRef.current.error
      });
      opts?.onFinish?.([null, resultRef.current.error]);
    }
    executeRef.current = void 0;
    status.current = "idle";
    isRunningCallbacks.current = false;
  }, [opts?.onError, opts?.onFinish, opts?.onSuccess]);
  const isPending = isTransitioning || isExecuting;
  useEffect(() => {
    if (isPending)
      return;
    handleCallbacks();
  }, [status.current, isPending]);
  useEffect(() => {
    return () => {
      if (executeRef.current !== void 0) {
        handleCallbacks();
      }
    };
  }, []);
  const final = calculateResultFromState({
    isPending,
    oldResult,
    result: resultRef.current,
    persistDataWhilePending: opts?.persistDataWhilePending,
    persistErrorWhilePending: opts?.persistErrorWhilePending
  });
  return {
    ...final,
    reset,
    execute,
    setOptimistic,
    executeFormAction
  };
};
export {
  useServerAction
};
